import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useRetainageReleases = (workOrderId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["retainage_releases", workOrderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wo_retainage_releases" as any)
        .select("*")
        .eq("work_order_id", workOrderId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!workOrderId,
  });
};

export const useProjectRetainage = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["project_retainage", projectId],
    queryFn: async () => {
      const { data: wos, error } = await supabase
        .from("work_orders")
        .select("id, number, amount, status, retainage_pct, retainage_released, retainage_status, vendors(name)" as any)
        .eq("project_id", projectId!)
        .neq("status", "cancelled")
        .neq("status", "rejected");
      if (error) throw error;
      return (wos ?? []) as any[];
    },
    enabled: !!user && !!projectId,
  });
};

export const useProjectRetainageReleases = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["project_retainage_releases", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_retainage_releases" as any)
        .select("*")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user && !!projectId,
  });
};

export const useCreateRetainageRelease = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (release: {
      company_id: string;
      work_order_id: string;
      release_amount: number;
      release_type: string;
      release_reason: string;
      lien_waiver_received: boolean;
      punch_list_complete: boolean;
      notes?: string;
      created_by?: string;
    }) => {
      const { data, error } = await supabase
        .from("wo_retainage_releases" as any)
        .insert(release as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["retainage_releases", variables.work_order_id] });
      qc.invalidateQueries({ queryKey: ["work_orders"] });
      qc.invalidateQueries({ queryKey: ["project_retainage"] });
    },
  });
};

export const useInitiateProjectRetainageRelease = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      projectId: string;
      amount: number;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc("initiate_retainage_release" as any, {
        p_project_id: params.projectId,
        p_amount: params.amount,
        p_notes: params.notes ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_retainage"] });
      qc.invalidateQueries({ queryKey: ["project_retainage_releases"] });
      qc.invalidateQueries({ queryKey: ["approval_counts"] });
    },
  });
};
